import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { DEFAULT_CONFIG, mergeConfig } from "@/config/defaults";

const SiteConfigContext = createContext({ config: DEFAULT_CONFIG });
export const useSiteConfig = () => useContext(SiteConfigContext);

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  return `${parseInt(m[1], 16)} ${parseInt(m[2], 16)} ${parseInt(m[3], 16)}`;
}

const loadedFonts = new Set();
function loadFont(name) {
  if (!name || loadedFonts.has(name)) return;
  loadedFonts.add(name);
  const id = `font-${name.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${name.replace(/\s+/g, "+")}:wght@300;400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

export function applyTheme(theme) {
  if (!theme) return;
  const root = document.documentElement.style;
  const set = (v, val) => { const rgb = hexToRgb(val); if (rgb) root.setProperty(v, rgb); };
  set("--brand-rgb", theme.brand);
  set("--brand-dark-rgb", theme.brandDark);
  set("--ai-rgb", theme.ai);
  set("--sos-rgb", theme.sos);
  if (theme.headingFont) { root.setProperty("--font-heading", `'${theme.headingFont}'`); loadFont(theme.headingFont); }
  if (theme.bodyFont) { root.setProperty("--font-body", `'${theme.bodyFont}'`); loadFont(theme.bodyFont); }
}

export function SiteConfigProvider({ children }) {
  const isPreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";
  const [data, setData] = useState({});

  useEffect(() => {
    api.get(`/site-config${isPreview ? "?mode=draft" : ""}`)
      .then((r) => setData(r.data?.data || {}))
      .catch(() => {});
  }, [isPreview]);

  // Live preview: apply config pushed from the editor via postMessage.
  useEffect(() => {
    if (!isPreview) return;
    const onMsg = (e) => {
      if (e.data && e.data.__sosConfig) setData(e.data.__sosConfig);
    };
    window.addEventListener("message", onMsg);
    window.parent?.postMessage({ __sosPreviewReady: true }, "*");
    return () => window.removeEventListener("message", onMsg);
  }, [isPreview]);

  const config = useMemo(() => mergeConfig(DEFAULT_CONFIG, data), [data]);

  useEffect(() => { applyTheme(config.theme); }, [config.theme]);

  return <SiteConfigContext.Provider value={{ config }}>{children}</SiteConfigContext.Provider>;
}
